import { useEffect, useState } from "react";
import { api, brl } from "../lib/api";
import { AdminGate, AdminShell } from "./AdminGate";
import { toast } from "sonner";
import {
  FileText, CalendarCheck, Wallet, Truck, Star, Loader2, RefreshCw, RotateCcw,
  Users, UserCheck, CheckCircle2, XCircle, Clock, Home as HomeIcon,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";

const STATUS_LABEL = {
  disponivel: "Disponível", aceita: "Aceita", a_caminho: "A caminho", chegou_origem: "Chegou na origem",
  carga_iniciada: "Carga iniciada", em_rota: "Em rota", chegou_destino: "Chegou ao destino",
  concluida: "Concluída", cancelada: "Cancelada",
};

const PERIODOS = [["diario", "DIÁRIO"], ["semanal", "SEMANAL"], ["mensal", "MENSAL"]];

function Dashboard({ user }) {
  const [periodo, setPeriodo] = useState("diario");
  const [dash, setDash] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/admin/dashboard?periodo=${periodo}`)
      .then((r) => setDash(r.data))
      .catch(() => toast.error("Falha ao carregar dashboard"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [periodo]);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);

  const reiniciar = async () => {
    if (!window.confirm(`Reiniciar a contagem ${periodo.toUpperCase()} do dashboard?\n\nSomente os indicadores deste período passam a contar a partir de agora. Nenhum dado (clientes, motoristas, corridas, contratos, pagamentos, avaliações) será apagado.`)) return;
    try {
      await api.post("/admin/dashboard/reiniciar", { periodo });
      toast.success("Contagem reiniciada — histórico preservado");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao reiniciar"); }
  };

  const c = dash?.contagem;
  const cards = c ? [
    { icon: Truck, label: "Solicitações de frete", value: c.fretes, testid: "dash-fretes" },
    { icon: HomeIcon, label: "Mudanças", value: c.mudancas, testid: "dash-mudancas" },
    { icon: FileText, label: "Total de solicitações", value: c.solicitacoes, testid: "dash-solicitacoes" },
    { icon: Clock, label: "Aguardando motorista", value: c.aguardando_motorista, testid: "dash-aguardando" },
    { icon: CheckCircle2, label: "Corridas aceitas", value: c.aceitas, testid: "dash-aceitas" },
    { icon: XCircle, label: "Corridas recusadas", value: c.recusadas, testid: "dash-recusadas" },
    { icon: XCircle, label: "Corridas canceladas", value: c.canceladas, testid: "dash-canceladas" },
    { icon: CalendarCheck, label: "Corridas concluídas", value: c.concluidas, testid: "dash-concluidas" },
    { icon: Users, label: "Clientes no período", value: c.clientes, sub: `${c.clientes_total} no total`, testid: "dash-clientes" },
    { icon: UserCheck, label: "Motoristas parceiros", value: c.motoristas, testid: "dash-motoristas" },
    { icon: Wallet, label: "Valores movimentados", value: brl(c.valores_movimentados), sub: `Sinais: ${brl(c.sinais_confirmados)}`, testid: "dash-valores" },
  ] : [];

  return (
    <AdminShell user={user} title="Dashboard">
      <div className="px-4 mt-4 space-y-4" data-testid="admin-dashboard">
        <div className="flex items-center gap-1.5" data-testid="dash-periodos">
          {PERIODOS.map(([v, l]) => (
            <button key={v} data-testid={`dash-periodo-${v}`} onClick={() => setPeriodo(v)}
              className={`flex-1 py-2.5 rounded-xl text-[11px] font-extrabold transition-all ${
                periodo === v ? "bg-navy text-[#F5B301]" : "bg-white text-slate-500 border border-slate-200"}`}>
              {l}
            </button>
          ))}
        </div>

        <div className="flex gap-2">
          <button data-testid="dash-atualizar-btn" onClick={load} disabled={loading}
            className="flex-1 py-3 rounded-xl bg-[#F5B301] text-navy font-heading font-extrabold text-xs flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-60">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} ATUALIZAR DADOS
          </button>
          <button data-testid="dash-reiniciar-btn" onClick={reiniciar}
            className="py-3 px-4 rounded-xl bg-white border border-red-200 text-red-500 font-heading font-bold text-xs flex items-center justify-center gap-1.5 active:scale-95 transition-transform">
            <RotateCcw className="w-3.5 h-3.5" /> REINICIAR CONTAGEM
          </button>
        </div>
        {dash?.reiniciado_em && (
          <p className="text-[10px] text-slate-400 -mt-2" data-testid="dash-reiniciado-info">
            Contagem {periodo} reiniciada em {new Date(dash.reiniciado_em).toLocaleString("pt-BR")} — histórico preservado.
          </p>
        )}

        {!dash && (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#F5B301]" /></div>
        )}

        {dash && (
          <>
            <div className="grid grid-cols-2 gap-2.5" data-testid="dash-cards">
              {cards.map(({ icon: Icon, label, value, sub, testid }) => (
                <div key={label} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid={testid}>
                  <Icon className="w-5 h-5 text-[#F5B301] mb-1.5" />
                  <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{label}</p>
                  <p className="font-heading font-extrabold text-navy text-lg leading-tight">{value}</p>
                  {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
                </div>
              ))}
            </div>

            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
              <p className="text-xs font-bold text-navy mb-3">Evolução — solicitações x corridas concluídas</p>
              <div className="h-52" data-testid="chart-evolucao">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={dash.series} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 9, fill: "#94a3b8" }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 9, fill: "#94a3b8" }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 10 }} />
                    <Area type="monotone" dataKey="solicitacoes" name="Solicitações" stroke="#1e3a5f" fill="#1e3a5f" fillOpacity={0.15} strokeWidth={2} />
                    <Area type="monotone" dataKey="concluidas" name="Concluídas" stroke="#F5B301" fill="#F5B301" fillOpacity={0.35} strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
              <p className="text-xs font-bold text-navy mb-3">Sinais confirmados (R$)</p>
              <div className="h-44" data-testid="chart-sinais">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dash.series} margin={{ top: 4, right: 4, left: -14, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 9, fill: "#94a3b8" }} />
                    <YAxis tick={{ fontSize: 9, fill: "#94a3b8" }} />
                    <Tooltip formatter={(v) => brl(v)} />
                    <Bar dataKey="valor" name="Sinais" fill="#F5B301" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        )}

        {stats && (
          <>
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
              <p className="text-xs font-bold text-navy mb-2.5">Corridas por status (geral)</p>
              <div className="flex flex-wrap gap-1.5" data-testid="corridas-status">
                {Object.entries(stats.corridas_por_status).map(([st, n]) => (
                  <span key={st} className="text-[10px] font-bold bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full" data-testid={`status-chip-${st}`}>
                    {STATUS_LABEL[st] || st}: {n}
                  </span>
                ))}
                {Object.keys(stats.corridas_por_status).length === 0 && (
                  <p className="text-xs text-slate-400">Nenhuma corrida ainda.</p>
                )}
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
              <p className="text-xs font-bold text-navy mb-2.5">Ranking de motoristas</p>
              <div className="space-y-2" data-testid="ranking-motoristas">
                {stats.ranking_motoristas.length === 0 && <p className="text-xs text-slate-400">Nenhum motorista cadastrado.</p>}
                {stats.ranking_motoristas.map((m, i) => (
                  <div key={m.driver_id} className="flex items-center justify-between" data-testid={`ranking-row-${m.driver_id}`}>
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-navy text-[#F5B301] text-[10px] font-extrabold flex items-center justify-center">{i + 1}</span>
                      <div>
                        <p className="text-xs font-bold text-navy">{m.nome}</p>
                        <p className="text-[10px] text-slate-400">{m.corridas_concluidas} corrida(s) concluída(s)</p>
                      </div>
                    </div>
                    <span className="flex items-center gap-1 text-xs font-bold text-[#F5B301]">
                      <Star className="w-3.5 h-3.5 fill-[#F5B301]" /> {m.nota.toFixed(1)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </AdminShell>
  );
}

export default function AdminStats() {
  return <AdminGate>{(user) => <Dashboard user={user} />}</AdminGate>;
}
